import type { ReactNode } from "react";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stepper } from "@freightsimple/ui-core/Controls/Stepper";
import { getHandlingUnitTypesForDropdown } from "@/Data/getHandlingUnitTypesForDropdown";
import type { HandlingUnitType } from "@freightsimple/generated-dashboard-openapi-client/models/HandlingUnitType";

interface HandlingUnitsInputProps {
  quantity: number;
  onQuantityChange: (_: number) => void;
  handlingUnitType: HandlingUnitType | undefined;
  onHandlingUnitTypeChange: (_: HandlingUnitType) => void;
  errorMessage: ReactNode | undefined;
  /** Aside shown in the field's message slot while there is no error. */
  hint?: ReactNode | undefined;
  forceValidation: boolean;
}

function LineItemsHandlingUnitsInput(props: HandlingUnitsInputProps) {
  const options = getHandlingUnitTypesForDropdown(props.quantity > 1);
  const placeholder =
    props.quantity > 1 ? "Select Handling Units" : "Select Handling Unit";

  return (
    <ShowFormErrors show={props.forceValidation}>
      {/* The label earns the row it occupies, and the row is what lines this
          field up with the dimension inputs in the next column — they each carry
          a one-line label ("Length (in)"), so without one here the stepper and
          dropdown ride up by a label's height. It has to be real text and it has
          to stay on one line: a blank label collapses to nothing (a
          whitespace-only block generates no line box, which is why the ` `
          this replaces never worked), and a wrapping one overshoots.
          `align="end"` sits the stepper and the dropdown on the same underline. */}
      <FormItem
        label="Number and type of handling units"
        error={props.errorMessage}
        hint={props.hint}
      >
        <Row gap={6} align="end">
          <Stepper
            value={props.quantity}
            onChange={props.onQuantityChange}
            min={1}
          />
          <Dropdown
            options={options}
            value={props.handlingUnitType}
            onChange={(v) =>
              props.onHandlingUnitTypeChange(v as HandlingUnitType)
            }
            placeholder={placeholder}
            width="full"
            data-testid="handlingUnitTypeControl"
          />
        </Row>
      </FormItem>
    </ShowFormErrors>
  );
}

export default LineItemsHandlingUnitsInput;
